import { useEffect, useRef, useState } from "react";
import { useSession } from "@/hooks/useSession";
import { usePatient } from "@/hooks/usePatient";
import {
  useMessages,
  useSendMessage,
  useMarkMessagesRead,
  type PatientMessageRow,
} from "@/hooks/useMessages";
import { AppLayout } from "@/components/Layout";
import { PageHeader } from "@/components/PageHeader";
import { LoadingScreen } from "@/components/LoadingScreen";
import { Button } from "@/components/Button";
import { Alert } from "@/components/Alert";
import { EmptyState } from "@/components/EmptyState";
import {
  MessageCategoryPicker,
  CategoryBadge,
  type SelectableCategory,
} from "@/components/MessageCategoryPicker";

const MAX_BODY_LEN = 5000;

const ChatIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function Messages() {
  const { session } = useSession();
  const patientQ = usePatient(session?.user.id);
  const patientId = patientQ.data?.id;
  const messagesQ = useMessages(patientId);
  const send = useSendMessage(patientId);
  const markRead = useMarkMessagesRead(patientId);

  const [draft, setDraft] = useState("");
  const [category, setCategory] = useState<SelectableCategory | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Mark unread inbound messages as read when the patient lands on this
  // page. Runs once per patient_id load. Side-effect only; we don't care
  // about the result count here.
  useEffect(() => {
    if (!patientId) return;
    markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  // Auto-scroll to the bottom of the thread when new messages arrive or
  // after a send completes. Uses requestAnimationFrame so the DOM has
  // rendered the new bubble before we scroll.
  useEffect(() => {
    if (!scrollRef.current) return;
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  }, [messagesQ.data?.length]);

  if (patientQ.isLoading) return <LoadingScreen />;
  const patient = patientQ.data;
  if (!patient || !session) {
    return (
      <AppLayout>
        <PageHeader title="Messages" />
        <Alert tone="error">Could not load your account.</Alert>
      </AppLayout>
    );
  }

  const messages = messagesQ.data ?? [];
  const charsLeft = MAX_BODY_LEN - draft.length;
  const canSend = draft.trim().length > 0 && category != null && !send.isPending;

  async function handleSend() {
    const body = draft.trim();
    if (!body || !category || send.isPending) return;
    try {
      await send.mutateAsync({ body, category });
      setDraft("");
      setCategory(null);
    } catch {
      // Error surfaced via send.error below.
    }
  }

  return (
    <AppLayout>
      <PageHeader
        title="Messages"
        subtitle="Secure messaging with your ForteHealth care team."
      />

      {/* Disclaimer about turnaround + urgency. Surfaced prominently because
          missed expectations on response time are the #1 messaging complaint
          in patient surveys. Also reinforces the portal-only convention
          so patients know not to email-reply to notifications. */}
      <Alert tone="info">
        Care-team replies typically arrive within <strong>1 business day</strong>.
        You'll get an email letting you know — sign in here to read &amp; reply,
        so your full conversation stays in one place. For urgent medical issues
        call 911 or go to your nearest ER — messaging is not for emergencies.
      </Alert>

      <div className="mt-4 bg-white border border-fh-border rounded-card overflow-hidden flex flex-col"
           style={{ minHeight: "60vh" }}>
        {/* Scroll region with the message thread. */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-4 md:p-6 flex flex-col gap-3"
          style={{ maxHeight: "65vh" }}
        >
          {messagesQ.isLoading ? (
            <div className="text-[13px] text-fh-gray text-center py-12">Loading messages…</div>
          ) : messages.length === 0 ? (
            <EmptyState
              icon={<ChatIcon />}
              title="No messages yet"
              body="Send your first message below — questions about your treatment, side effects, scheduling, or anything else. Your care team will reply within 1 business day."
            />
          ) : (
            messages.map((m) => <MessageBubble key={m.id} message={m} />)
          )}
        </div>

        {/* Compose region pinned to bottom. Stays visible while the thread
            scrolls so the patient never loses their draft. */}
        <div className="border-t border-fh-border p-3 md:p-4 bg-fh-cream">
          {send.error && (
            <div className="mb-2">
              <Alert tone="error">{send.error.message}</Alert>
            </div>
          )}
          {/* Category picker — REQUIRED before sending. Drives routing:
              medical → clinician inbox, support/billing → care team. */}
          <div className="mb-3">
            <MessageCategoryPicker value={category} onChange={setCategory} variant="full" />
          </div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, MAX_BODY_LEN))}
            onKeyDown={(e) => {
              // Cmd/Ctrl + Enter sends. Plain Enter is a newline so the
              // patient can write multi-paragraph messages.
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={
              category === "medical"
                ? "Describe your symptoms or question for the clinician…"
                : category === "billing"
                ? "Describe your billing question or issue…"
                : category === "support"
                ? "What can we help you with?"
                : "Pick a category above, then type your message…"
            }
            rows={4}
            disabled={category == null}
            className="w-full px-3.5 py-3 text-[14px] text-fh-ink bg-white border border-fh-border rounded-[10px] focus:outline-none focus:ring-2 focus:ring-fh-green focus:border-fh-green resize-none disabled:bg-fh-sand/40 disabled:cursor-not-allowed"
          />
          <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
            <span className="text-[11.5px] text-fh-gray-light">
              {!category ? (
                "Pick a category above to start typing"
              ) : (
                <>
                  <kbd className="px-1 py-0.5 rounded border border-fh-border text-[10px] font-mono">⌘ Enter</kbd> to send
                  {charsLeft < 500 && (
                    <span className={charsLeft < 100 ? "text-amber-700 ml-2" : "ml-2"}>
                      · {charsLeft} characters left
                    </span>
                  )}
                </>
              )}
            </span>
            <Button
              loading={send.isPending}
              disabled={!canSend}
              onClick={handleSend}
            >
              {category === "medical" ? "Send to clinician" : category === "billing" ? "Send to billing" : "Send to care team"}
            </Button>
          </div>
        </div>
      </div>

      <p className="mt-3 text-[12px] text-fh-gray-light leading-relaxed">
        Messages are secured in transit and at rest, and sent to
        care@fortehealth.co. Replies arrive by email and (soon) in this
        thread. For password resets or sensitive account changes, use the
        Account page instead.
      </p>
    </AppLayout>
  );
}

function MessageBubble({ message }: { message: PatientMessageRow }) {
  const isOutbound = message.direction === "outbound";
  const failed = message.status === "failed";
  const time = new Date(message.created_at).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  // For inbound bubbles, surface who the message is from so a patient can
  // tell apart "care team general" replies from a specific clinician.
  const inboundSenderLabel = !isOutbound
    ? message.sender_display_name ||
      (message.sender_role === "provider" ? "Your provider"
        : message.sender_role === "system" ? "ForteHealth"
        : "ForteHealth Care Team")
    : null;

  return (
    <div className={`flex ${isOutbound ? "justify-end" : "justify-start"}`}>
      <div
        className={[
          "max-w-[80%] md:max-w-[70%] rounded-[16px] px-4 py-2.5 leading-relaxed",
          isOutbound
            ? failed
              ? "bg-red-50 text-red-900 border border-red-200"
              : "bg-fh-green-dark text-white"
            : "bg-fh-sage text-fh-ink",
        ].join(" ")}
      >
        {inboundSenderLabel && (
          <div className="text-[11px] font-semibold text-fh-green-dark mb-0.5 flex items-center gap-2 flex-wrap">
            <span>{inboundSenderLabel}</span>
            {message.category !== "general" && <CategoryBadge category={message.category} />}
          </div>
        )}
        {isOutbound && message.category !== "general" && (
          <div className="mb-1">
            <CategoryBadge category={message.category} />
          </div>
        )}
        <div className="text-[14px] whitespace-pre-wrap break-words">
          {message.body}
        </div>
        <div
          className={[
            "mt-1.5 text-[11px] flex items-center gap-2",
            isOutbound
              ? failed
                ? "text-red-700"
                : "text-white/70"
              : "text-fh-gray",
          ].join(" ")}
        >
          <span>{time}</span>
          {isOutbound && (
            <>
              <span>·</span>
              <span>
                {failed
                  ? "Failed to send"
                  : message.status === "delivered"
                    ? "Delivered"
                    : "Sent"}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
